import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  AdminKurssloeyfeFragment,
  KurssloeyfeAdminPageData,
  KurssloeyfeAdminPageDataVariables,
  ReorderKurssloeyfeElementInput,
  ReorderKurssloeyfeElementVariables,
} from "__generated__/types";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { urls } from "common/urls";
import {
  ListElement,
  ListElementKurssloeyfeElement,
} from "domain/admin/common/AdministrationList";
import { CreateContent } from "domain/admin/common/CreateContent";
import { CreateContentFooterBox } from "domain/admin/common/CreateContentFooterBox";
import { adminKurssloeyfeFragment } from "domain/admin/fragments";
import { noDeletedStatusFilter } from "domain/admin/util";
import { getParentFromContentContext, isInSyllabus } from "domain/common/utils";
import { contentBreadcrumbFragment } from "domain/pages/breadcrumbs";
import * as React from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";

const AdministrationList = React.lazy(() =>
  import("domain/admin/common/AdministrationList").then((it) => ({
    default: it.AdministrationList,
  })),
);

const GET_KURSSLOEYFE = gql`
  query KurssloeyfeAdminPageData($kurssloeyfeId: ID!) {
    kurssloeyfeById(id: $kurssloeyfeId) {
      ...ContentBreadcrumbFragment
      ...AdminKurssloeyfeFragment
    }
  }
  ${contentBreadcrumbFragment}
  ${adminKurssloeyfeFragment}
`;

const MUTATION = gql`
  mutation ReorderKurssloeyfeElement($input: ReorderKurssloeyfeElementInput!) {
    reorderKurssloeyfeElement(input: $input) {
      kurssloeyfe {
        elements {
          id
          content {
            id
            title
            ... on Article {
              youtubeVideoId
            }
          }
        }
      }
    }
  }
`;

const convertToList = (
  data: AdminKurssloeyfeFragment["elements"],
  parentId: string,
): ListElement[] => {
  return data.map((it) => ({
    category: "KurssloeyfeElement",
    type: it.content.__typename,
    id: it.id,
    status: it.content.status,
    title: it.content.title,
    kurssloeyfeElementId: it.id,
    kurssloeyfeParentId: parentId,
    kurssloeyfeId: it.parent.id,
    contentId: it.content.id,
  }));
};

type RouteProps = {
  kurssloeyfeId: string;
};

export const KurssloeyfeAdminPage: React.FC = () => {
  const { kurssloeyfeId } = useParams<RouteProps>();

  if (!kurssloeyfeId) {
    throw new Error("Unexpected error: Missing kurssloeyfeId");
  }

  const { data, loadingOrError, refetch } = useSimpleQuery<
    KurssloeyfeAdminPageData,
    KurssloeyfeAdminPageDataVariables
  >(GET_KURSSLOEYFE, {
    variables: {
      kurssloeyfeId,
    },
  });

  const [mutate, { error }] = useMutation<
    ReorderKurssloeyfeElementInput,
    ReorderKurssloeyfeElementVariables
  >(MUTATION);

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.kurssloeyfeById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const { title, id: contentId, elements, context } = data.kurssloeyfeById;
  const parent = getParentFromContentContext(context);

  if (!parent) {
    throw Error("Found kurssløyfe without parent element.");
  }

  const handleSaveListOrder = async (
    id: string,
    moveToAfterElement: ListElementKurssloeyfeElement | null,
  ) => {
    await mutate({
      variables: {
        input: {
          kurssloeyfeId: contentId,
          elementId: id,
          moveToAfterElementId: moveToAfterElement?.id,
        },
      },
    });

    return refetch();
  };

  const filteredList = elements.filter((e) =>
    noDeletedStatusFilter(e.content.status),
  );

  return (
    <AdministrationLayout title={title} context={context ?? []}>
      <Link to={urls.kurssloeyfe(kurssloeyfeId)}>Studentvisning</Link>
      {elements.length === 0 && <p>Kurssløyfen har ikke noe innhold enda.</p>}
      {filteredList.length > 0 && (
        <AdministrationList
          showElementIcons
          isInSyllabus={isInSyllabus(context)}
          elements={convertToList(filteredList, parent.id)}
          onSaveListOrder={handleSaveListOrder}
          complete={refetch}
          canDelete
        />
      )}
      <MutationLoadingOrError
        // TODO: Do we want to show loading indication when changing order?
        loading={false}
        error={error}
      />
      <CreateContentFooterBox
        buttonText="Legg til innhold"
        content={(props) => (
          <CreateContent {...props} parent="kurssloeyfe" parentId={contentId} />
        )}
        onUpdated={() => {
          void refetch();
        }}
      />
    </AdministrationLayout>
  );
};
